import React from 'react';
import * as Slider from '@radix-ui/react-slider';
import * as Accordion from '@radix-ui/react-accordion';
import { ChevronDown, ChevronRight } from 'lucide-react';
import ShareDialog from './ShareDialog';
import { SWATCH_COLORS_HEX } from '../utils/dildoGeometry';
import { PRESETS } from '../utils/presets';

// ── Custom Slider ────────────────────────────────────────────────────────────
const NeonSlider = ({ id, label, value, min, max, step = 0.01, onChange, unit = '' }) => (
  <div className="flex flex-col gap-2">
    <div className="flex justify-between items-center">
      <label className="text-[10px] uppercase tracking-[0.18em] text-gray-400">{label}</label>
      <span className="text-[11px] font-mono text-[#00F0FF]">
        {typeof value === 'number' ? value.toFixed(2) : value}{unit}
      </span>
    </div>
    <Slider.Root
      className="relative flex items-center select-none touch-none w-full h-5"
      min={min} max={max} step={step} value={[value]}
      onValueChange={([v]) => onChange(v)}
    >
      <Slider.Track className="relative grow h-[2px] bg-gray-800">
        <Slider.Range className="absolute h-full bg-[#00F0FF]" />
      </Slider.Track>
      <Slider.Thumb
        className="block w-3 h-3 bg-[#00F0FF] focus:outline-none"
        style={{ boxShadow: '0 0 8px rgba(0,240,255,0.9)' }}
      />
    </Slider.Root>
  </div>
);

// ── Color Swatches ──────────────────────────────────────────────────────────
const ColorSwatches = ({ value, onChange }) => (
  <div className="flex gap-[3px] mt-1">
    {SWATCH_COLORS_HEX.map((color, i) => {
      const t = i / (SWATCH_COLORS_HEX.length - 1);
      const selected = Math.abs(value - t) < 0.075;
      return (
        <button
          key={i}
          onClick={() => onChange(t)}
          style={{ backgroundColor: color }}
          className={`flex-1 h-5 transition-all duration-100 ${selected ? 'ring-1 ring-[#00F0FF] ring-offset-1 ring-offset-[#080B0F]' : ''}`}
        />
      );
    })}
  </div>
);

// ── Preset Buttons ──────────────────────────────────────────────────────────
const PRESET_KEYS = Object.keys(PRESETS);

const PresetBar = ({ onSelect }) => (
  <div className="flex gap-1 flex-wrap">
    {PRESET_KEYS.map(key => (
      <button
        key={key}
        onClick={() => onSelect(key)}
        className="px-2.5 py-1 text-[9px] uppercase tracking-[0.15em] border border-gray-700/70 text-gray-500 hover:border-[#00F0FF]/50 hover:text-[#00F0FF] transition-all duration-150"
      >
        {key}
      </button>
    ))}
  </div>
);

// ── Accordion Section ────────────────────────────────────────────────────────
const Section = ({ id, title, children }) => (
  <Accordion.Item value={id} className="border-b border-[#00F0FF]/12">
    <Accordion.Trigger
      className="flex w-full items-center justify-between py-4 text-[10px] uppercase tracking-[0.2em] text-gray-400 hover:text-[#00F0FF] transition-colors group"
    >
      {title}
      <ChevronDown size={12} className="text-[#00F0FF]/50 transition-transform duration-200 group-data-[state=open]:rotate-180" />
    </Accordion.Trigger>
    <Accordion.Content className="overflow-hidden data-[state=open]:animate-accordion-down data-[state=closed]:animate-accordion-up">
      <div className="flex flex-col gap-5 pb-5">
        {children}
      </div>
    </Accordion.Content>
  </Accordion.Item>
);

// ── Main Panel ───────────────────────────────────────────────────────────────
const ControlPanel = ({ params, isPanelOpen, onTogglePanel, onUpdate, onPreset }) => {
  return (
    <aside
      data-testid="control-panel"
      className={`absolute bottom-0 right-0 w-full h-[50vh] md:top-16 md:bottom-auto md:w-[360px] md:h-[calc(100vh-4rem)] z-30 flex flex-col transition-transform duration-300 ease-in-out ${isPanelOpen ? 'translate-y-0 md:translate-x-0' : 'translate-y-full md:translate-y-0 md:translate-x-full'
        }`}
      style={{
        background: 'rgba(6,9,14,0.94)',
        borderLeft: '1px solid rgba(0,240,255,0.12)',
        borderTop: '1px solid rgba(0,240,255,0.12)',
        backdropFilter: 'blur(20px)',
      }}
    >
      {/* Toggle tab */}
      <button
        onClick={onTogglePanel}
        className="absolute -top-[34px] right-4 md:top-[64px] md:-left-[34px] md:right-auto flex items-center justify-center w-[34px] h-[34px] bg-[#030405]/95 border border-[#00F0FF]/30 text-[#00F0FF] hover:bg-[#00F0FF]/15 z-50 transition-colors md:border-r-0"
      >
        <ChevronRight size={18} className={`hidden md:block transition-transform duration-300 ${!isPanelOpen ? 'rotate-180' : ''}`} />
        <ChevronDown size={18} className={`md:hidden transition-transform duration-300 ${!isPanelOpen ? 'rotate-180' : ''}`} />
      </button>

      {/* Header */}
      <div className="px-6 py-4 border-b border-[#00F0FF]/12 flex items-center gap-2">
        <div className="w-1.5 h-4" style={{ background: '#00F0FF', boxShadow: '0 0 8px #00F0FF' }} />
        <span className="text-[10px] uppercase tracking-[0.3em] text-[#00F0FF]">Parameters</span>
        <span className="ml-auto text-[9px] text-gray-600 font-mono tracking-widest">v1.0</span>
      </div>

      {/* Scrollable content */}
      <div className="flex-1 overflow-y-auto px-6 custom-scrollbar">
        <Accordion.Root type="multiple" defaultValue={['presets', 'shape', 'details', 'material']}>

          <Section id="presets" title="Presets">
            <PresetBar onSelect={onPreset} />
          </Section>

          <Section id="shape" title="Shape">
            <NeonSlider id="length" label="Length" value={params.length} min={0.5} max={2.5} onChange={v => onUpdate('length', v)} />
            <NeonSlider id="shaftGirth" label="Shaft Girth" value={params.shaftGirth} min={0.3} max={1.5} onChange={v => onUpdate('shaftGirth', v)} />
            <NeonSlider id="glansScale" label="Glans Size" value={params.glansScale} min={0.9} max={2.0} onChange={v => onUpdate('glansScale', v)} />
            <NeonSlider id="coronaDepth" label="Corona Definition" value={params.coronaDepth} min={0} max={1.0} onChange={v => onUpdate('coronaDepth', v)} />
            <NeonSlider id="baseFlare" label="Base Flare" value={params.baseFlare} min={0} max={1.0} onChange={v => onUpdate('baseFlare', v)} />
          </Section>

          <Section id="details" title="Details">
            <NeonSlider id="curvature" label="Curvature" value={params.curvature} min={0} max={1.0} onChange={v => onUpdate('curvature', v)} />
            <NeonSlider id="taper" label="Shaft Taper" value={params.taper} min={0} max={1.0} onChange={v => onUpdate('taper', v)} />
            <NeonSlider id="ridges" label="Ridges / Texture" value={params.ridges} min={0} max={1.0} onChange={v => onUpdate('ridges', v)} />
            <NeonSlider id="veins" label="Veins" value={params.veins} min={0} max={1.0} onChange={v => onUpdate('veins', v)} />
          </Section>

          <Section id="material" title="Material">
            <div className="flex flex-col gap-2">
              <span className="text-[10px] uppercase tracking-[0.18em] text-gray-400">Color</span>
              <ColorSwatches value={params.colorTone} onChange={v => onUpdate('colorTone', v)} />
            </div>
            <NeonSlider id="colorTone" label="Color Tone" value={params.colorTone} min={0} max={1.0} onChange={v => onUpdate('colorTone', v)} />
            <NeonSlider id="roughness" label="Surface Roughness" value={params.roughness} min={0.05} max={0.95} onChange={v => onUpdate('roughness', v)} />
            <NeonSlider id="sssStrength" label="Subsurface / Glow" value={params.sssStrength} min={0} max={1.0} onChange={v => onUpdate('sssStrength', v)} />
          </Section>

        </Accordion.Root>
      </div>

      {/* Footer */}
      <div className="px-6 py-3 border-t border-[#00F0FF]/10 space-y-3">
        <ShareDialog params={params} isPanel={true} />
        <p className="text-[9px] text-gray-700 uppercase tracking-widest leading-relaxed">
          For educational, design &amp; artistic use only
        </p>
      </div>
    </aside>
  );
};

export default ControlPanel;
